import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button";
import { apiClient } from "../../lib/api-client";
import { useTransferLead } from "./api";
import { useAuth } from "../auth/AuthProvider";

interface TeamMember { id: string; name: string; email: string; role: string; isActive: boolean }

interface Props {
  workspaceSlug: string | null;
  workspaceId: string;
  leadId: string;
  currentAssigneeId: string | null;
  onClose: () => void;
  onTransferred: () => void;
}

export function TransferDialog({
  workspaceSlug,
  workspaceId,
  leadId,
  currentAssigneeId,
  onClose,
  onTransferred,
}: Props) {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["team", workspaceSlug, "for-transfer"],
    queryFn: () =>
      apiClient.get<{ users: TeamMember[] }>("/api/users", {
        params: { workspace: workspaceId },
      }),
  });
  const transfer = useTransferLead(workspaceSlug);
  const [toUserId, setToUserId] = useState("");
  const [reason, setReason] = useState("");

  // Eligible teammates: active, not the current assignee. We do NOT hide
  // the logged-in user globally — admins can intentionally assign to
  // themselves — but if they're already the assignee, that option vanishes
  // (you can't transfer a lead to its current owner).
  const candidates = (data?.users ?? []).filter(
    (u) => u.isActive && u.id !== currentAssigneeId
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!toUserId) return;
    await transfer.mutateAsync({
      leadId,
      toUserId,
      ...(reason ? { reason } : {}),
    });
    onTransferred();
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <header className="dialog-header">
          <h3>Transfer lead</h3>
          <button className="dialog-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <form onSubmit={onSubmit} className="form-stack">
          <label className="field">
            <span className="field-label">Assign to</span>
            <select value={toUserId} onChange={(e) => setToUserId(e.target.value)} required>
              <option value="">— pick a teammate —</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                  {user && u.id === user.id ? " (you)" : ""} ({u.role})
                </option>
              ))}
            </select>
            {candidates.length === 0 ? (
              <span className="muted" style={{ fontSize: 12 }}>
                No other active teammates available to transfer to.
              </span>
            ) : null}
          </label>
          <label className="field">
            <span className="field-label">Reason (optional)</span>
            <input
              className="field-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. better fit / out of office"
            />
          </label>
          {transfer.isError ? <div className="form-error">{(transfer.error as Error).message}</div> : null}
          <div className="dialog-actions">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!toUserId || transfer.isPending}>
              {transfer.isPending ? "Transferring…" : "Transfer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
